import { useCurrencies, useMyWalletAndAssets, useProfile } from "@/hooks";
import { useP2PInputClass } from "./useP2PInputClass";
import { useAppSelector } from "@/store/hooks";
import type { PaymentMethod } from "@/interfaces/payment-method";
import { useEffect, useMemo } from "react";
import { PriceType } from "@/interfaces/exchange";
import { normalizeCurrency, removeTrailingToken } from "@/utils";
import { TradeType } from "@/interfaces/trade";
import { formatCurrency } from "@/utils/formatCurrency";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { LedgerAccountType, type WalletAsset } from "@/interfaces/asset";

export const StepRenderer = ({
  errors,
  step,
  form,
  set,
  availablePaymentMethods,
}: any) => {
  const inputClass = useP2PInputClass();
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";

  const navigate = useNavigate();

  const { data } = useMyWalletAndAssets(user?.id as string);

  console.log("USER", data);

  const nonZeroBalances =
    data?.assets
      ?.map((asset: any) => {
        const availableAccount = asset.accounts?.find(
          (account: any) => account.type === LedgerAccountType.AVAILABLE,
        );

        return {
          ...asset,
          availableAccount,
          balance: availableAccount?.balance ?? "0",
        };
      })
      .filter((asset: any) => Number(asset.balance) > 0) ?? [];

  const { data: currencies } = useCurrencies();
  const { data: profile } = useProfile(userId);
  const { data: myAssets } = useMyWalletAndAssets(userId);

  console.log(nonZeroBalances);

  const selectedFiat =
    currencies?.find((c) => c.id === form.fiatCurrencyId)?.iso || "USD";

  const selectedAsset = myAssets?.assets?.find(
    (a: WalletAsset) => a.id === form.assetId,
  )?.currency;

  // const currency = normalizeCurrency(selectedAsset);

  console.log("SELECTED FIAT", selectedFiat);
  console.log("SELECTED ASSET", selectedAsset);

  //  const selectedAsset = nonZeroBalances?.find((a) => a.id === form.assetId);
  const filteredPaymentMethods = useMemo(() => {
    return (
      profile?.paymentMethods?.filter(
        (pm) => pm.currencyId === form.fiatCurrencyId && pm.active,
      ) || []
    );
  }, [profile?.paymentMethods, form.fiatCurrencyId]);

  useEffect(() => {
    if (!form.fiatCurrencyId) return;

    const validIds = filteredPaymentMethods.map((pm) => pm.id);

    set({
      paymentMethodIds: form.paymentMethodIds.filter((id: string) =>
        validIds.includes(id),
      ),
    });
  }, [form.fiatCurrencyId]);

  useEffect(() => {
    if (!filteredPaymentMethods.length) return;

    const defaults = filteredPaymentMethods
      .filter((pm) => pm.isDefault)
      .map((pm) => pm.id);

    if (defaults.length) {
      set({ paymentMethodIds: defaults });
    }
  }, [form.fiatCurrencyId]);

  useEffect(() => {
    if (!availablePaymentMethods?.length) return;

    const defaults = availablePaymentMethods
      .filter((pm: PaymentMethod) => pm.isDefault)
      .map((pm: PaymentMethod) => pm.id);

    if (form.paymentMethodIds.length === 0 && defaults.length > 0) {
      set({ paymentMethodIds: defaults });
    }
  }, [availablePaymentMethods]);

  switch (step) {
    case 0:
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Select Asset</h2>

          <select
            className={inputClass}
            value={form.assetId}
            onChange={(e) => set({ assetId: e.target.value })}
          >
            <option value="">Select asset</option>
            {nonZeroBalances?.map((asset: WalletAsset) => (
              <option key={asset.id} value={asset.id}>
                {normalizeCurrency(asset.currency ?? "")} {""}(
                {removeTrailingToken(asset.token.name)})
              </option>
            ))}
          </select>
          {/* <div className="text-xl text-emerald-500">
            {formatCurrency(selectedAsset.balance)}{" "}
          </div> */}
          {errors.assetId && (
            <p className="text-xs text-red-500 mt-1">{errors.assetId}</p>
          )}

          <select
            className={inputClass}
            value={form.fiatCurrencyId}
            onChange={(e) => set({ fiatCurrencyId: e.target.value })}
          >
            <option value="">Select fiat currency</option>

            {currencies?.map((currency) => (
              <option key={currency.id} value={currency.id}>
                {currency?.iso} ({currency?.symbol})
              </option>
            ))}
          </select>
          {errors.fiatCurrencyId && (
            <p className="text-xs text-red-500 mt-1">{errors.fiatCurrencyId}</p>
          )}

          <select
            className={inputClass}
            value={form.type}
            onChange={(e) => set({ type: e.target.value as TradeType })}
          >
            <option value="">Select trade type</option>
            <option value={TradeType.SELL}>Sell</option>
            <option value={TradeType.BUY}>Buy</option>
          </select>
        </div>
      );

    case 1: // Pricing
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Pricing</h2>

          <select
            className={inputClass}
            value={form.priceType}
            onChange={(e) => {
              const newType = e.target.value as PriceType;
              set({
                priceType: newType,
                price: newType === PriceType.FIXED ? form.price : "",
                floatingMargin:
                  newType === PriceType.FLOATING
                    ? form.floatingMargin || "100"
                    : "",
              });
            }}
          >
            <option value={PriceType.FIXED}>Fixed Price</option>
            {/* <option value={PriceType.FLOATING}>
              Floating (Market + Margin)
            </option> */}
          </select>

          {form.priceType === PriceType.FIXED && (
            <input
              type="text"
              inputMode="decimal"
              className={inputClass}
              placeholder={`Fixed Price (${selectedFiat})`}
              value={form.price}
              onChange={(e) => set({ price: e.target.value })}
            />
          )}

          {/* {form.priceType === PriceType.FLOATING && (
            <div>
              <input
                type="text"
                inputMode="decimal"
                className={inputClass}
                placeholder="Floating Margin (%) - e.g. 102.5"
                value={form.floatingMargin}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*\.?\d{0,4}$/.test(val)) set({ floatingMargin: val });
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">
                100 = market price. 70–130 range recommended.
              </p>
            </div>
          )} */}
        </div>
      );

    case 2: // Limits + Payment Window
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Limits &amp; Window</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <input
                className={inputClass}
                type="text"
                inputMode="decimal"
                placeholder={`Min Amount`}
                value={form.minAmount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*\.?\d{0,8}$/.test(v)) set({ minAmount: v });
                }}
              />
              {form.minAmount && (
                <span>
                  {selectedFiat} {formatCurrency(form.minAmount * form.price)}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <input
                className={inputClass}
                type="text"
                inputMode="decimal"
                placeholder={`Max Amount`}
                value={form.maxAmount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*\.?\d{0,8}$/.test(v)) set({ maxAmount: v });
                }}
              />

              {form.maxAmount && (
                <span>
                  {selectedFiat} {formatCurrency(form.maxAmount * form.price)}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <input
                className={inputClass}
                type="text"
                inputMode="decimal"
                placeholder={`Total Amount`}
                value={form.totalAmount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*\.?\d{0,8}$/.test(v)) set({ totalAmount: v });
                }}
              />

              {form.totalAmount && (
                <span>
                  {selectedFiat} {formatCurrency(form.totalAmount * form.price)}
                </span>
              )}
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">
                Payment Window (minutes)
              </label>
              <input
                type="number"
                min={5}
                max={30}
                className={inputClass}
                value={form.paymentWindow}
                onChange={(e) => set({ paymentWindow: Number(e.target.value) })}
              />
            </div>
          </div>
        </div>
      );

    case 3:
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Payment Methods</h2>

          {errors.paymentMethodIds && (
            <p className="text-xs text-red-500 mt-1">
              {errors.paymentMethodIds}
            </p>
          )}

          {/* ❗ No currency selected */}
          {!form.fiatCurrencyId && (
            <p className="text-sm text-muted-foreground">
              Please select a fiat currency first
            </p>
          )}

          {form.fiatCurrencyId && filteredPaymentMethods.length === 0 && (
            <div className="flex flex-col gap-3">
              <div className="p-4 border rounded-xl bg-yellow-50 text-sm text-yellow-700">
                `You have not created any payment method for this fiat
                currency:(
                {selectedFiat}) `.
              </div>
              <Button onClick={() => navigate("/dashboard/p2p?tab=payments")}>
                Add Payment Method
              </Button>
            </div>
          )}

          {form.fiatCurrencyId && filteredPaymentMethods.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing payment methods for selected currency
            </p>
          )}

          {filteredPaymentMethods.map((pm: PaymentMethod) => (
            <label
              key={pm.id}
              className={`flex gap-3 border rounded-xl p-4 cursor-pointer transition ${
                form.paymentMethodIds.includes(pm.id)
                  ? "border-primary bg-primary/5"
                  : "border-muted"
              }`}
            >
              <input
                type="checkbox"
                checked={form.paymentMethodIds.includes(pm.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    set({
                      paymentMethodIds: [...form.paymentMethodIds, pm.id],
                    });
                  } else {
                    set({
                      paymentMethodIds: form.paymentMethodIds.filter(
                        (id: string) => id !== pm.id,
                      ),
                    });
                  }
                }}
              />

              <div>
                <p className="font-medium">{pm.bank.name}</p>
                <p className="text-sm text-muted-foreground">
                  {pm.accountNumber}
                </p>
              </div>
            </label>
          ))}
        </div>
      );

    case 4:
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Terms</h2>

          {errors.terms && (
            <p className="text-xs text-red-500 mt-1">{errors.terms}</p>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              className="px-3 py-2 border rounded"
              onClick={() =>
                set({
                  terms: [
                    ...form.terms,
                    {
                      title: "",
                      description: "",
                      order: form.terms.length,
                      isActive: true,
                    },
                  ],
                })
              }
            >
              Add Term
            </button>
          </div>

          <div className="space-y-3">
            {form.terms.map((term: any, index: number) => (
              <div key={index} className="border p-3 rounded space-y-2">
                {/* HEADER + DELETE */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Order: {index}
                  </span>

                  <button
                    type="button"
                    className="text-xs text-red-500 disabled:opacity-40"
                    disabled={form.terms.length === 1}
                    onClick={() => {
                      if (form.terms.length === 1) return;

                      const updated = form.terms
                        .filter((_: any, i: number) => i !== index)
                        .map((t: any, i: number) => ({
                          ...t,
                          order: i, // 🔥 re-index orders after deletion
                        }));

                      set({ terms: updated });
                    }}
                  >
                    Remove
                  </button>
                </div>

                {/* TITLE */}
                <input
                  className={inputClass}
                  placeholder="Title (optional)"
                  value={term.title || ""}
                  onChange={(e) => {
                    const copy = [...form.terms];
                    copy[index].title = e.target.value;
                    set({ terms: copy });
                  }}
                />

                {/* DESCRIPTION */}
                <textarea
                  className={inputClass}
                  placeholder="Description"
                  value={term.description}
                  onChange={(e) => {
                    const copy = [...form.terms];
                    copy[index].description = e.target.value;
                    set({ terms: copy });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );

    case 5: {
      const selectedAsset = nonZeroBalances?.find(
        (a: WalletAsset) => a.id === form.assetId,
      );

      const isSell = form.type === "SELL";

      return (
        <div className="space-y-5">
          <h2 className="text-lg sm:text-xl font-semibold">
            Review Advertisement
          </h2>

          <div className="rounded-xl border bg-muted/20 p-4 space-y-3">
            {/* ... existing summary ... */}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {/* Add these lines */}
              <div>
                <p className="text-muted-foreground">Payment Window</p>
                <p className="font-medium">{form.paymentWindow} minutes</p>
              </div>

              {form.priceType === PriceType.FLOATING && (
                <div>
                  <p className="text-muted-foreground">Floating Margin</p>
                  <p className="font-medium">{form.floatingMargin}%</p>
                </div>
              )}
            </div>
          </div>

          {/* Terms preview */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Terms</p>

            <div className="space-y-2">
              {form.terms?.map((term: any, i: number) => (
                <div key={i} className="border rounded-lg p-3 bg-background/50">
                  <p className="text-sm font-medium">
                    {term.title || `Term ${i + 1}`}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {term.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};
